import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, useRouterState } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";

import { useAuthSync } from "../shared/auth/useAuthSync";
import { RootNotFound } from "../shared/layout/NotFound";
import { RouteError } from "../shared/layout/RouteError";
import { AppProviders } from "../shared/layout/AppProviders";

export interface RouterContext {
  queryClient: QueryClient;
}

/** FE-04 FR-FE04-01: owner manifest under /portal, seller manifest everywhere else. */
function useAreaManifest(pathname: string) {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const href = pathname.startsWith("/portal")
      ? "/manifest-owner.webmanifest"
      : "/manifest-seller.webmanifest";
    if (link.getAttribute("href") !== href) link.setAttribute("href", href);
  }, [pathname]);
}

// The editor is a full-screen area without the seller tab bar (FE-11 §2).
const NO_BOTTOM_NAV = /^\/app\/(login|register|sites\/[^/]+\/editor)(\/|$)/;

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useAreaManifest(pathname);
  useAuthSync();
  const hasBottomNav = pathname.startsWith("/app") && !NO_BOTTOM_NAV.test(pathname);
  return (
    <AppProviders aboveBottomNav={hasBottomNav}>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </AppProviders>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: RootNotFound,
  errorComponent: RouteError,
});
